import { buildPrompt } from '@/ai/utils/promptBuilder'
import { getLanguageFromPath } from '@/ai/utils/helpers'
import { systemMessages } from '@/ai/prompts/common/systemMessages'
import { workflowFromCodeExample } from '@/ai/prompts/common/examples'
import { getJsonArrayOutputFormatting, getJsonOutputFormatting } from '@/ai/prompts/common/formatting'
import { createUserMessage, createSystemMessage, createAssistantMessage } from '@/ai/utils/promptUtils'

export const buildWorkflowFromCodePrompt = ({ codeSnippets, workflowSteps, description, stream }) => {
  const messages = []

  const systemMessage = buildPrompt([{ content: systemMessages.EVENT_MODELING_REVERSE_ENGINEERING }])
  messages.push(createSystemMessage(systemMessage))

  if (workflowSteps && workflowSteps.length > 0) {
    messages.push(createAssistantMessage(JSON.stringify({ workflow: workflowSteps })))
  } else {
    messages.push(createAssistantMessage(JSON.stringify({ workflow: [] })))
  }

  const promptSections = []

  if (codeSnippets && codeSnippets.length > 0) {
    promptSections.push({
      title: 'Code Snippets for Analysis',
      content: `
        Analyze the following code snippets from a software repository. Your goal is to extract a complete-as-possible
        description of domain events, the roles that trigger them, associated commands (with their input fields/write models),
        necessary queries (with their output fields/read models) that inform those commands, and involved domain entities
        (marking one as the aggregate root for each command/event context, and listing their fields).
      `,
      spacingAfter: 1
    })

    codeSnippets.forEach((snippet, index) => {
      promptSections.push({
        title: `File path: ${snippet.path} | Name: ${snippet.name || ''}`,
        contentType: 'codeblock',
        language: getLanguageFromPath(snippet.path),
        content: snippet.content,
        spacingAfter: index === codeSnippets.length - 1 ? 2 : 1
      })
    })
  } else {
    promptSections.push({
      title: 'Code Snippets for Analysis',
      content: `
        No specific code snippets were provided. If this is unexpected, please review the input.
        If intended, make your best judgment based on general software principles for common domains.
      `
    })
  }

  if (description && description.trim() !== '') {
    promptSections.push({
      title: 'User-Provided Context and Hints',
      content: `
        In addition to the code snippets, use the following description to guide your analysis.
        Pay special attention to any entities, processes, or areas the user has highlighted.
        This context is a critical guide for interpreting the code's purpose.

        **Description:**
        "${description}"
      `
    })
  }

  promptSections.push({
    title: 'Task: Update and Extend Workflow Components from Code Chunk',
    content: [
      `You are analyzing a chunk of code to identify and refine domain workflow components. You have been provided
       with a set of "previously extracted workflow steps" (in the preceding assistant message, under a "workflow" key).
       Your task is to:
       1. Analyze the **current code snippets** provided below.
       2. For each **state-changing operation** (typically corresponding to a create, update, or delete action on a
          domain entity) you identify in the **current code snippets**:
          a. **Check if a corresponding event (matched by 'role' and 'eventDescription') already
          exists** in the "previously extracted workflow steps".
          b. **If it exists:** **UPDATE** that existing entry. Add or refine its 'command' (name, dataFields),
          'queries' (add new queries or update existing ones by merging dataFields), or 'entities' (add new entities
          or update existing ones by merging dataFields). When merging dataFields for commands, queries, or entities,
          aim for a comprehensive union of all fields found across different code snippets for that specific item.
          Ensure only one entity is marked \`isAggregateRoot: true\` per event context.
          c. **If it does NOT exist:** **CREATE** a new workflow step entry for this newly identified event and
          its related components (command, queries, entities) based on the current code snippets.
       3. Return **ONLY the workflow steps that are new or need to be updated. **Crucially, your response should NOT
          include any of the "previously extracted workflow steps" that were NOT modified by the current
          code chunk. **Your response should only be a "delta" containing new and updated items.`,
      '',
      '**Key fields to extract/update for each event-centric workflow step:**',
      '1.  **role**: (String) The actor initiating the action.',
      `2.  **eventDescription**: (String, PastTense, human-readable words) The name of the domain event.
       This **must** describe a state change (create, update, delete). **Do NOT use verbs like "Retrieved",
       "Fetched", "Viewed", "Got", "Listed" for event descriptions.** *This + role is the primary key for matching.*`,
      `3.  **command**: (Object or null) \`{ name: "CommandName", dataFields: [{ fieldName: "camelCaseFieldName" }] }\`.
       **A valid eventDescription (state change) should typically have an associated command.**
       If an operation is purely a query (data retrieval), its \`command\` should be \`null\`, and it should NOT be listed
       as an event-centric step itself unless it triggers a subsequent state change (which would be a separate event).`,
      `4.  **queries**: (Array of Objects) Each \`{ name: "QueryName", dataFields: [{ fieldName: "camelCaseFieldName",
       indexed: (optional Boolean) }] }\`. These are informational and may be associated with a command/event.`,
      `5.  **entities**: (Array of Objects) Each \`{ name: "EntityName", isAggregateRoot: boolean, dataFields:
       [{ fieldName: "camelCaseFieldName", primaryKey: (optional Boolean), tags: (optional String, e.g., "REQUIRED") }] }\`.
       Exactly one \`isAggregateRoot: true\` per event context.`,
      `6.  **gwt**: (Array of Strings) One or more "Given-When-Then" acceptance tests. **Each string in this
       array must be a complete sentence** in the format "Given [context], when [action], then [outcome]."\``,
      '',
      '**Merging Strategy for `dataFields` (for commands, queries, entities):**',
      `- When updating an existing item (command, query, or entity) that is already associated with an event, if new fields
       are found in the current code snippet for that item, **add the new unique fields** to its \`dataFields\` list.`,
      `- If a field with the same \`fieldName\` already exists, ensure its other attributes (like \`indexed\` for
       queries, or \`tags\` and \`primaryKey\` for entities) are consistent or take the most complete/accurate
       information found across snippets. For example, if one snippet suggests a field is a \`primaryKey\` and another
       doesn't, assume it is a \`primaryKey\`. If one suggests \`tags: "REQUIRED"\` and another doesn't, assume it is required.`,
      '',
      '**Important:**',
      '- If the current code chunk reveals no new information or no new events, simply return an empty array.',
      `- Command, query and entity names, event descriptions, should be extracted from code but presented in a
       human-readable format (e.g., "Payment Method" from "paymentMethod").`,
      `- All command, query and entity data fields should be extracted from code but presented in a
       camel case format (e.g., "userId").`,
      `- Workflow steps in the response **MUST** be arranged chronologically. After identifying domain events
       arrange them in a logical, **business‐relevant chronological order**. Use your understanding of typical
       domain flows to infer which events should occur first, next, and last. For example, “User Created” should
       come before “User Updated,” and “Order Placed” should come before "Payment Completed."`,
      `- The response **MUST NOT** exceed output tokens limit. If the response is supposed to be larger than that,
       stop at some reasonable point where you have complete JSON and return the response.`
    ]
  })

  promptSections.push({
    title: 'Explanation and Notes on Domain Events and Roles',
    content: [
      'Focus only on true domain events.',
      `Your task is to analyze the provided source code snippets to identify **state-changing domain events**
       and the **roles** (actors or systems) that trigger them.`,
      `A state-changing event is defined as an outcome of a create/update/delete operation on a core aggregate
       or entity (e.g., creating an order, updating a user profile, or deleting an invoice).`,
      'For each event, provide exactly two keys:',
      `**role**: The actor (human user type, an external system, or an internal service) that invokes the
       command leading to the event. Infer this from class names, method signatures, or comments. (Max 18 characters.)`,
      `**description**: A concise, past-tense phrase describing only the change itself, using separate words
       (not concatenated). Do **not** repeat the role name in this field. For example, if the role is "User" and
       the change is updating the last login type, then **description** should be: "Updated Last Login Type".`,
      'Do **NOT** include:',
      'Any file‐system calls, data‐fetch methods, logging, metrics updates, or other purely technical operations.',
      'Anything that does not represent a real change to the business state of an aggregate.',
      `A state-changing event must reflect a business‐relevant change to an aggregate/entity. For example, "Order Created"
       or "Payment Completed" are valid. Words should remain separate (e.g., "Order Created", not "OrderCreated").`,
      `Do **not** prefix the description with the role. For instance, if **role** is "User", do **not** write
       "User Updated Last Login Type"; write only "Updated Last Login Type".`,
      'If a code snippet contains both a create/update/delete and incidental reads, capture only the create/update/delete part.',
      'Ignore any actions that merely read data or update non‐domain state. Examples of things to exclude:',
      'Retrieving or returning data (e.g., "Retrieved Current User", "Fetched Order List").',
      'Internal or technical changes (e.g., "Cache Updated", "Token Refreshed").',
      'Authentication/authorization checks that do not change domain state (e.g., "User Authenticated" alone).',
      'Logging, metrics, or UI/view rendering (e.g., "Rendered Login Page").'
    ]
  })

  promptSections.push({
    title: 'Explanation and Notes on Commands (Write Models)',
    content: [
      `The Write Model consists of the set of input fields (parameters or data transfer
       object properties) required to execute the associated command. These fields represent
       the data an actor (user or system) would need to provide.`,
      `For each event in the workflow refer to its associated **Command** name. Analyze the provided code snippets,
       specifically looking at method signatures of the command handlers or service methods that implement the command,
       definitions of Data Transfer Objects (DTOs), Request objects, or Command objects used as parameters for these methods.`,
      'The number of input fields per command can vary, but it is usually between 2 and 8.',
      `A data field name must be at most 30 characters long and should reflect the names
       found in the code, but in camel case format (e.g., "userId" as "userId", "product_name" as "productName").`,
      'If a command takes a complex object as a parameter, list the properties of that object as the data fields.',
      `Only include fields that are direct inputs to the command. Do not include fields that are derived
       internally or come from system context (like current user ID if not explicitly passed).`,
      `If a command has multiple overloaded methods, try to identify the primary one
       or the one that seems most relevant to a user-driven or system-driven workflow action.`
    ]
  })

  promptSections.push({
    title: 'Explanation and Notes on Queries (Read Models)',
    content: [
      `The Read Model provides the actor (human or system) with the necessary information
       *before* they invoke the associated command. It represents a query or a view of the system state.`,
      `For each event in the workflow refer to its associated **Read Model** name
       (e.g., "GetUserDetails", "FetchOpenOrders"). Analyze the provided code snippets, specifically looking at
       return types of query methods in services, query handlers, or repositories and definitions of
       Data Transfer Objects (DTOs) or Response objects returned by these query methods or GET API endpoints.`,
      `List the data fields that make up this Read Model. The data fields are the result of the query, but not
       the parameters needed to perform the query.`,
      `Tag each data field with "INDEXED" if it seems like a field that
       would commonly be used for filtering or searching in that query (e.g., IDs, status fields, names).`,
      `A data field name must be at most 30 characters long and should reflect the names
       found in the code, but in camel case format (e.g., "userId" as "userId", "product_name" as "productName").`
    ]
  })

  promptSections.push({
    title: 'Explanation and Notes on Entities',
    content: [
      `For each identified entity/aggregate, list its name and its main attributes or properties as suggested by
       the code (e.g., class members, fields, or properties from its definition).`,
      'Focus on data-holding classes/structures that represent the core nouns of the domain.',
      `A data field (attributes) name must be at most 30 characters long and should reflect the names
       found in the code, but in camel case format (e.g., "userId" as "userId", "product_name" as "productName").`,
      'Each object in the array represents a single identified entity.',
      `Only include direct properties/fields of the entity. Do not list methods, constructors,
       or complex behavioral aspects as data fields in this step.`,
      `If an entity seems to be defined across multiple provided snippets (e.g., partial classes),
       consolidate its data fields under a single entity entry.`,
      `If a class seems to be a DTO (Data Transfer Object) or a simple data container primarily used for
       input/output rather than a core domain entity, you may choose to include it if it represents
       a significant piece of domain data, but prioritize clear aggregate roots and core entities.`
    ]
  })

  promptSections.push({
    title: 'Explanation and Notes on Given-When-Then (GWT) Scenarios',
    content: [
      'For each state-changing event, you must also generate at least one acceptance test in the "Given-When-Then" style.',
      `**Each GWT scenario must be a single, complete string** within the 'gwt' array. Do not split the "Given",
       "When", and "Then" parts into separate array items.`,
      'The structure of each GWT string should be: `"Given [context], when [action], then [outcome]."`',
      '- **Given**: Describes the precondition or the context. (e.g., "Given a user has items in their cart...")',
      `- **When**: Describes the action being taken, usually corresponding to the command.
       (e.g., "...when they submit the 'Place Order' command...")`,
      `- **Then**: Describes the expected outcome or the state change, usually corresponding to the event.
       (e.g., "...then an 'Order Placed' event occurs and a new order is saved.")`,
      `Infer these conditions from the code. Look for validation checks (for the 'Given' part), the main operation of
       the function (for the 'When' part), and the successful result (for the 'Then' part).`
    ]
  })

  promptSections.push({
    title: 'Important Notes',
    contentType: 'bullet',
    content: [
      `If a code chunk does not reveal any clear domain events or related components,
       return an empty array for the top-level "workflow" property.`,
      `Extract names (\`eventDescription\`, command \`name\`, query \`name\`, entity \`name\`) from the code,
       but return them in a more readable format (e.g., "paymentMethod" as "Payment Method").`,
      `Extract data field names of commands, queries and entities from the code,
       but return them in a more camel case format (e.g., "userId" as "userId", "product_name" as "productName").`,
      `Focus on core domain logic. Exclude purely technical operations, UI elements, or generic utilities unless
       they directly represent a domain concept.`,
      'Adhere strictly to the JSON output format provided in the example.'
    ]
  })

  const additionalFormattingInstructions = `
    The output must be a single JSON object. This object must contain exactly one top-level key: "updatesAndAdditions".
    The value of "updatesAndAdditions" must be an array of workflow step objects.
    This array should ONLY contain steps that are brand new or were updated based on the current code chunk.
    If no new or updated steps are found, the "updatesAndAdditions" array MUST be empty ([]).
    Each object in the array must follow the full, detailed structure (role, eventDescription, command, queries, entities).
  `

  promptSections.push({
    title: 'Formatting',
    flattenLines: false,
    content: stream
      ? getJsonArrayOutputFormatting(JSON.stringify(workflowFromCodeExample.updatesAndAdditions))
      : getJsonOutputFormatting('workflow', JSON.stringify(workflowFromCodeExample), additionalFormattingInstructions)
  })

  const userContent = buildPrompt(promptSections)
  messages.push(createUserMessage(userContent))

  return messages
}
